'use client';

import cx from 'classnames';

import { type GrantSearchRecord, type SearchRecordDate } from '~/ingest/search/model/searchRecord';
import { FullBleedCTAScope, FullBleedCTATrigger } from '~/ui/components/fullBleedCta';
import { Column, Grid } from '~/ui/components/grid';
import { Icon } from '~/ui/components/icon';
import { Link } from '~/ui/components/link';
import listStyles from '~/ui/components/list/list.module.scss';
import { Breakpoint } from '~/ui/styles/grid';
import truncateStyles from '~/ui/styles/truncate.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { formatDate } from '~/ui/utils/formatDate';
import { capitalizeFirstLetter } from '~/utils/format';

import styles from './grantSearchResult.module.scss';
import globalSearchResultStyles from '../global/globalSearchResult.module.scss';

export interface GrantSearchResultProps {
  result: GrantSearchRecord;
  position: number;
  queryId?: string;
}

export function GrantSearchResult({
  result,
  position,
  queryId,
}: GrantSearchResultProps): React.ReactElement {
  const grantee = result.grantee ?? '';
  const description = result.description ?? '';
  const location = result.location
    ? result.location.country === 'United States'
      ? result.location.state
      : result.location.country
    : '';
  const year = formatSearchRecordDate(result.date);
  const metadata: Record<string, React.ReactNode> = {
    ...(description && { 'Grant description': description }),
    ...(location && { Location: location }),
    'Year awarded': year,
  };

  return (
    <FullBleedCTAScope>
      <li className={listStyles.item}>
        <Grid className={cx(styles.grid, typographyStyles.bodySmall)}>
          <Column
            span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 2 }}
            offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
            className={styles.column}
          >
            <div className={styles.title}>
              <h3 className={cx(typographyStyles.bodyLarge, truncateStyles.truncate3)}>
                {grantee}
              </h3>
              <div>
                <Icon name="expanding-arrow-right" className={styles.mobileIcon} />
              </div>
            </div>

            <dl
              className={cx(
                globalSearchResultStyles.metadata,
                styles.metadata,
                typographyStyles.bodySmall,
              )}
            >
              {Object.entries(metadata).map(([key, value], index) => (
                <div className={globalSearchResultStyles.metadataValue} key={index}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </Column>

          <Column
            span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 3 }}
            className={styles.column}
          >
            <span className={styles.text}>{capitalizeFirstLetter(description)}</span>
          </Column>

          <Column
            span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
            className={styles.column}
          >
            <span className={styles.text}>{location}</span>
          </Column>

          <Column
            span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
            className={styles.column}
          >
            <div>
              <span className={styles.text}>{year}</span>

              <FullBleedCTATrigger>
                <Link
                  className={styles.desktopIcon}
                  href={result.href}
                  withIcon
                  data-insights-object-id={result.objectID}
                  data-insights-position={position}
                  data-insights-query-id={queryId}
                />
              </FullBleedCTATrigger>
            </div>
          </Column>
        </Grid>
      </li>
    </FullBleedCTAScope>
  );
}

function formatSearchRecordDate(date: SearchRecordDate): string {
  // Dates are expected to be in UTC and not the client's timezone
  return formatDate(date._timestamp || date.value, {
    formatOptions: { day: undefined, month: undefined, timeZone: 'UTC' },
  });
}
